import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../Utils/SbUtils';
import ReviewAlertEditorContext from '../Context/ReviewAlertEditorContext';
import ReviewAlertSidebar from '../ReviewAlert/ReviewAlertSidebar';
import ReviewAlertPreview from '../ReviewAlert/ReviewAlertPreview';
import CustomizerHeader from '../Global/Headers/CustomizerHeader';

const ReviewAlertEditor = ({
    editorTopLoader,
    editorNotification,
    editorConfirmDialog,
    isPro,
    upsellModal,
    sources,
    sourcesCount,
    allowedTiers,
    fbModal,
    fbManualModal,
    bulkSr,
    freeRet,
    addSModal,
    freeRetModal,
    apis,
    apiLimits
}) => {
    const sbCustomizer = window.sb_customizer;

    // Store initial settings for change detection (deep clone to avoid reference issues)
    const initialSettings = useRef(
        JSON.parse(JSON.stringify(sbCustomizer?.popupData?.settings || sbCustomizer?.reviewAlerts?.defaults || {}))
    );

    // Store initial name for change detection
    const initialName = useRef(
        sbCustomizer?.popupData?.popup_info?.name || __('New Review Alert', 'reviews-feed')
    );

    // Popup data state
    const [popupData, setPopupData] = useState(sbCustomizer?.popupData || {
        popup_info: {
            id: 0,
            name: __('New Review Alert', 'reviews-feed'),
            status: 'inactive'
        },
        settings: sbCustomizer?.reviewAlerts?.defaults || {}
    });

    // Track popup publish status ('active' = published, 'inactive' = draft)
    const [popupStatus, setPopupStatus] = useState(
        sbCustomizer?.popupData?.popup_info?.status || 'inactive'
    );

    // Current sidebar section - use PHP-defined default if available
    const [currentSidebarSection, setCurrentSidebarSection] = useState(
        sbCustomizer?.popupEditor?.defaultTab || 'sb-popup-tab'
    );
    const sidebarSection = useMemo(
        () => ({ currentSidebarSection, setCurrentSidebarSection }),
        [currentSidebarSection, setCurrentSidebarSection]
    );

    // Active device for preview
    const [device, setDevice] = useState('desktop');
    const editorActiveDevice = useMemo(
        () => ({ device, setDevice }),
        [device, setDevice]
    );

    // Header classes state (for embed modal z-index handling)
    const [headerCustomClasses, setHeaderCustomClasses] = useState([]);
    const headerClasses = useMemo(
        () => ({ headerCustomClasses, setHeaderCustomClasses }),
        [headerCustomClasses, setHeaderCustomClasses]
    );

    // Preview reviews state - start with loading=true to show skeleton immediately
    const [previewReviews, setPreviewReviews] = useState([]);
    const [totalReviews, setTotalReviews] = useState(0);
    const [unfilteredTotal, setUnfilteredTotal] = useState(0);
    const [averageRating, setAverageRating] = useState(null);
    const [isLoadingReviews, setIsLoadingReviews] = useState(true);
    const fetchTimeoutRef = useRef(null);

    // Preview expanded state - controlled from sidebar section clicks
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);

    /**
     * Fetch preview reviews from backend
     * Called when sources, filters, or sort settings change
     */
    const fetchPreviewReviews = useCallback(() => {
        // Clear any pending fetch
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        // Set loading state IMMEDIATELY to disable UI interactions during debounce
        // This prevents race conditions when rapidly changing sources
        setIsLoadingReviews(true);

        // Debounce the fetch to avoid rapid API calls
        fetchTimeoutRef.current = setTimeout(() => {
            const formData = {
                action: 'sbr_review_alert_preview_reviews',
                nonce: sbCustomizer.nonce,
                settings: JSON.stringify(popupData.settings)
            };

            SbUtils.ajaxPost(
                sbCustomizer.ajaxHandler,
                formData,
                (data) => {
                    if (data?.data?.reviews) {
                        setPreviewReviews(data.data.reviews);
                    }
                    if (data?.data?.totalReviews !== undefined) {
                        setTotalReviews(data.data.totalReviews);
                    }
                    if (data?.data?.unfilteredTotal !== undefined) {
                        setUnfilteredTotal(data.data.unfilteredTotal);
                    }
                    if (data?.data?.averageRating !== undefined) {
                        setAverageRating(data.data.averageRating);
                    }
                    setIsLoadingReviews(false);
                },
                null, // Don't show loader in header
                null, // Don't show notification
                null
            );
        }, 500); // 500ms debounce
    }, [popupData.settings, sbCustomizer]);

    // Serialize settings for deep comparison in useEffect dependency array
    const sourcesJson = JSON.stringify(popupData.settings?.sources);
    const filtersJson = JSON.stringify(popupData.settings?.filters);
    const sortJson = JSON.stringify(popupData.settings?.sort);

    // Fetch reviews when settings that affect reviews change
    useEffect(() => {
        // Only fetch if sources are defined - no sources means no reviews to fetch
        // Filters and sort only apply when sources exist
        const hasSources = popupData.settings?.sources?.length > 0;

        if (hasSources) {
            fetchPreviewReviews();
        } else {
            // No sources - show empty state (don't fetch, no fallback to all sources)
            setPreviewReviews([]);
            setTotalReviews(0);
            setUnfilteredTotal(0);
            setAverageRating(0);
            setIsLoadingReviews(false);
        }

        // Cleanup on unmount
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
        // Note: fetchPreviewReviews is excluded from deps because it's a stable callback
        // that only uses refs and setState. Including it would cause infinite loops
        // since it depends on popupData.settings which is also a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourcesJson, filtersJson, sortJson]);

    // Update popup setting
    const updatePopupSetting = (key, value) => {
        setPopupData(prev => ({
            ...prev,
            settings: {
                ...prev.settings,
                [key]: value
            }
        }));
    };

    // Update nested popup setting
    const updateNestedPopupSetting = (parentKey, key, value) => {
        setPopupData(prev => ({
            ...prev,
            settings: {
                ...prev.settings,
                [parentKey]: {
                    ...prev.settings[parentKey],
                    [key]: value
                }
            }
        }));
    };

    /**
     * Check if popup has empty visibility settings
     * Returns true if in "specific" mode with no pages/categories/custom_post_types selected
     */
    const hasEmptyVisibility = () => {
        const visibility = popupData.settings?.visibility || {};
        const displayOn = visibility?.display_on || 'specific';

        // Only check if in "specific" mode
        if (displayOn !== 'specific') {
            return false;
        }

        // Check if any pages/categories/custom_post_types are selected
        const specific = visibility?.specific || {};
        const hasPages = (specific?.pages || []).length > 0;
        const hasCategories = (specific?.categories || []).length > 0;
        const hasPostTypes = (specific?.custom_post_types || []).length > 0;

        return !hasPages && !hasCategories && !hasPostTypes;
    };

    /**
     * Get visibility location count text for confirmation dialogs
     * Returns a user-friendly string like "12 locations" or "all pages"
     */
    const getVisibilityCountText = () => {
        const visibility = popupData.settings?.visibility || {};
        const displayOn = visibility?.display_on || 'specific';

        // Count items in a visibility group
        const countItems = (group) => {
            if (!group) return 0;
            const pages = (group.pages || []).length;
            const categories = (group.categories || []).length;
            const postTypes = (group.custom_post_types || []).length;
            return pages + categories + postTypes;
        };

        // If display_on is 'all', show "all pages"
        if (displayOn === 'all') {
            const excludeCount = countItems(visibility?.excluded);
            if (excludeCount > 0) {
                return __('all pages', 'reviews-feed') + ` (${excludeCount} ` + __('excluded', 'reviews-feed') + ')';
            }
            return __('all pages', 'reviews-feed');
        }

        // Specific pages mode
        const count = countItems(visibility?.specific);
        if (count === 0) {
            return __('0 locations', 'reviews-feed');
        }
        return count + ' ' + (count === 1 ? __('location', 'reviews-feed') : __('locations', 'reviews-feed'));
    };

    /**
     * Perform the actual save operation
     * @param {boolean} exit - Whether to exit after saving
     * @param {Function} onSuccess - Optional callback with saved popup ID
     */
    const performSave = (exit = false, onSuccess = null) => {
        const formData = {
            action: 'sbr_review_alert_save',
            nonce: sbCustomizer.nonce,
            id: popupData.popup_info.id || '',
            name: popupData.popup_info.name,
            settings: JSON.stringify(popupData.settings)
        };

        const notificationsContent = {
            success: {
                icon: 'success',
                text: __('Review alert saved successfully', 'reviews-feed')
            }
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            (data) => {
                // On error, don't update initial settings - preserves unsaved changes detection
                if (data?.success === false || data?.error) {
                    return;
                }

                let savedPopupId = popupData.popup_info.id;

                if (data?.data?.popup?.id && !popupData.popup_info.id) {
                    savedPopupId = data.data.popup.id;
                    setPopupData(prev => ({
                        ...prev,
                        popup_info: {
                            ...prev.popup_info,
                            id: data.data.popup.id
                        }
                    }));
                    // Update URL without reload
                    const newUrl = sbCustomizer.builderUrl + '&popup_id=' + data.data.popup.id;
                    window.history.replaceState({}, '', newUrl);
                } else if (!popupData.popup_info.id && !data?.data?.popup?.id) {
                    // New popup was created but backend didn't return ID - show error
                    editorNotification?.setNotification({
                        active: true,
                        type: 'error',
                        icon: 'error',
                        text: __('Review alert saved but ID not returned. Please refresh and try again.', 'reviews-feed')
                    });
                    return; // Don't call onSuccess without valid ID
                }
                // Update initial settings and name after successful save (deep clone to avoid reference issues)
                initialSettings.current = JSON.parse(JSON.stringify(popupData.settings));
                initialName.current = popupData.popup_info?.name;

                if (exit) {
                    setTimeout(() => {
                        goBackToList();
                    }, 500);
                }

                // Call success callback with the saved popup ID
                if (onSuccess && savedPopupId) {
                    onSuccess(savedPopupId);
                }
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        );
    };

    /**
     * Save popup with visibility validation
     * Only shows confirmation dialog if popup is PUBLISHED with no pages selected.
     * Draft alerts can be saved without visibility configured (no confirmation needed).
     */
    const savePopup = (exit = false) => {
        // Only show empty visibility warning if alert is currently published
        // Draft alerts can be saved without visibility configured
        if (popupStatus === 'active' && hasEmptyVisibility()) {
            const confirmDialogInfo = {
                active: true,
                heading: __('Your review alert is not visible on any pages', 'reviews-feed'),
                description: __('This review alert is published but won\'t appear anywhere on your site. Add pages in the Visibility settings, or save now and configure later.', 'reviews-feed'),
                confirm: {
                    text: __('Save Anyway', 'reviews-feed'),
                    type: 'primary',
                    icon: 'success',
                    onConfirm: () => performSave(exit)
                },
                cancel: {
                    text: __('Go to Visibility Settings', 'reviews-feed'),
                    type: 'secondary',
                    onCancel: () => setCurrentSidebarSection('sb-popup-visibility-tab')
                }
            };
            editorConfirmDialog.setConfirmDialog(confirmDialogInfo);
            return;
        }

        // No visibility issue or draft alert, save directly
        performSave(exit);
    };

    // Go back to list
    const goBackToList = () => {
        window.location.href = sbCustomizer.popupsPageUrl;
    };

    // Handle name change from header
    const handleNameChange = (newName) => {
        setPopupData(prev => ({
            ...prev,
            popup_info: {
                ...prev.popup_info,
                name: newName
            }
        }));
    };

    // Check if there are unsaved changes (settings or name)
    const hasUnsavedChanges = () => {
        const settingsChanged = JSON.stringify(initialSettings.current) !== JSON.stringify(popupData.settings);
        const nameChanged = initialName.current !== popupData.popup_info?.name;
        return settingsChanged || nameChanged;
    };

    /**
     * Toggle popup status via AJAX
     * @param {string} newStatus - 'active' or 'inactive'
     * @param {number} overrideId - Optional popup ID (used when saving new popup then publishing)
     * @param {Function} onSuccess - Callback after successful status change
     */
    const togglePopupStatus = (newStatus, overrideId = null, onSuccess = null) => {
        const popupId = overrideId || popupData.popup_info.id;

        if (!popupId) {
            // New popup - need to save first before we can toggle status
            editorNotification?.setNotification({
                active: true,
                icon: 'warning',
                text: __('Please save the review alert first before publishing.', 'reviews-feed')
            });
            return;
        }

        const formData = {
            action: 'sbr_review_alert_toggle_status',
            nonce: sbCustomizer.nonce,
            id: popupId
        };

        const notificationsContent = {
            success: {
                icon: 'success',
                text: newStatus === 'active'
                    ? __('Review alert published successfully!', 'reviews-feed')
                    : __('Review alert reverted to draft.', 'reviews-feed')
            }
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            (data) => {
                if (data?.data?.newStatus) {
                    setPopupStatus(data.data.newStatus);
                    // Update popup_info status as well
                    setPopupData(prev => ({
                        ...prev,
                        popup_info: {
                            ...prev.popup_info,
                            status: data.data.newStatus
                        }
                    }));
                }
                if (onSuccess) {
                    onSuccess();
                }
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        );
    };

    /**
     * Handle Publish button click
     * Shows confirmation dialog, then saves + publishes
     */
    const handlePublish = () => {
        // Check for empty visibility first
        if (hasEmptyVisibility()) {
            const confirmDialogInfo = {
                active: true,
                heading: __('Your review alert is not visible on any pages', 'reviews-feed'),
                description: __('Publishing will activate the review alert, but it won\'t appear anywhere until you add pages in Visibility settings.', 'reviews-feed'),
                confirm: {
                    text: __('Publish Anyway', 'reviews-feed'),
                    type: 'primary',
                    icon: 'arrow-top',
                    onConfirm: () => {
                        // Save first, then toggle status in callback (ensures ID is available)
                        performSave(false, (savedId) => {
                            togglePopupStatus('active', savedId);
                        });
                    }
                },
                cancel: {
                    text: __('Go to Visibility Settings', 'reviews-feed'),
                    type: 'secondary',
                    onCancel: () => setCurrentSidebarSection('sb-popup-visibility-tab')
                }
            };
            editorConfirmDialog.setConfirmDialog(confirmDialogInfo);
            return;
        }

        // Show publish confirmation dialog with location count
        const locationText = getVisibilityCountText();
        const isEmpty = hasEmptyVisibility();
        const description = isEmpty
            ? __( 'No pages configured. This alert won\'t be visible anywhere.', 'reviews-feed' )
            : __('This review alert will start appearing on', 'reviews-feed') + ' ' + locationText + ' ' + __('once published.', 'reviews-feed');
        const confirmDialogInfo = {
            active: true,
            heading: __('Ready to publish?', 'reviews-feed'),
            description: description,
            confirm: {
                text: __('Publish', 'reviews-feed'),
                type: 'primary',
                icon: 'arrow-top',
                onConfirm: () => {
                    // Save first, then toggle status in callback (ensures ID is available)
                    performSave(false, (savedId) => {
                        togglePopupStatus('active', savedId);
                    });
                }
            },
            cancel: {
                text: __('Cancel', 'reviews-feed'),
                type: 'secondary'
            }
        };
        editorConfirmDialog.setConfirmDialog(confirmDialogInfo);
    };

    /**
     * Handle Revert to Draft button click
     * Shows confirmation dialog, then unpublishes
     */
    const handleUnpublish = () => {
        const locationText = getVisibilityCountText();
        const isEmpty = hasEmptyVisibility();

        // Different description for empty vs configured visibility
        const description = isEmpty
            ? __('This review alert is published but not visible anywhere (no locations configured). Reverting to draft will unpublish it without affecting your site.', 'reviews-feed')
            : __('This review alert is currently live on', 'reviews-feed') + ' ' + locationText + '. ' + __('Reverting will stop it from appearing on your site until you publish again.', 'reviews-feed');

        const confirmDialogInfo = {
            active: true,
            heading: __('Revert to draft?', 'reviews-feed'),
            description: description,
            confirm: {
                text: __('Revert to Draft', 'reviews-feed'),
                type: 'primary',
                icon: 'reset',
                onConfirm: () => {
                    togglePopupStatus('inactive');
                }
            },
            cancel: {
                text: __('Cancel', 'reviews-feed'),
                type: 'secondary'
            }
        };
        editorConfirmDialog.setConfirmDialog(confirmDialogInfo);
    };

    return (
        <ReviewAlertEditorContext.Provider
            value={{
                sbCustomizer,
                popupData,
                setPopupData,
                updatePopupSetting,
                updateNestedPopupSetting,
                savePopup,
                goBackToList,
                sidebarSection,
                editorActiveDevice,
                editorTopLoader,
                editorNotification,
                editorConfirmDialog,
                isPro,
                upsellModal,
                sources,
                sourcesCount,
                allowedTiers,
                fbModal,
                fbManualModal,
                bulkSr,
                freeRet,
                addSModal,
                freeRetModal,
                apis,
                apiLimits,
                previewReviews,
                totalReviews,
                unfilteredTotal,
                isLoadingReviews,
                isPreviewExpanded,
                setIsPreviewExpanded
            }}
        >
            <section className={
                'sb-customizer-header sb-header sb-fs ' +
                (headerCustomClasses.length > 0 ? headerCustomClasses.join(' ') : '')
            }>
                <div className="sb-header-content sb-fs">
                    <CustomizerHeader
                        editorTopLoader={editorTopLoader}
                        itemName={popupData?.popup_info?.name || __('New Review Alert', 'reviews-feed')}
                        onNameChange={handleNameChange}
                        onSave={savePopup}
                        onBack={goBackToList}
                        hasChanges={hasUnsavedChanges}
                        editorConfirmDialog={editorConfirmDialog}
                        editorNotification={editorNotification}
                        sbCustomizer={sbCustomizer}
                        headerClasses={headerClasses}
                        hideEmbed={true}
                        status={popupStatus}
                        onPublish={handlePublish}
                        onUnpublish={handleUnpublish}
                    />
                </div>
                {
                    editorTopLoader?.loader &&
                    <div className="sb-loadingbar-ctn"></div>
                }
            </section>

            <section className="sb-cutomizer-ctn sb-fs">
                <ReviewAlertSidebar />
                <ReviewAlertPreview
                    settings={popupData.settings}
                    reviews={previewReviews}
                    totalReviews={totalReviews}
                    averageRating={averageRating}
                    isLoading={isLoadingReviews}
                />
            </section>
        </ReviewAlertEditorContext.Provider>
    );
};

export default ReviewAlertEditor;
